import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../chrome/AppShell';
import useDeviceTelemetry, { DeviceTelemetry } from '../../../DeviceTelemetry/hooks/useDeviceTelemetry';
import useDeviceKeys from '../../../DeviceManagement/components/hooks/useDeviceKeys';
import { Crumb, SectionCard } from '../../components/forms';
import { PageHeadActions } from '../../components/nav';
import { TCard, TEmpty, TSkeleton } from '../../components/table';
import { Frow } from '../../components/roster';
import { seen } from './shared';
import './devices.css';

/*
 * Read-only vitals for one device. :deviceId is the device KEY id
 * (key_id string) — the endpoint filters device_telemetry.device_key_id.
 * Polls every 10s like the classic view.
 */

const POLL_MS = 10000;

const humanKey = (k: string) =>
    k.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').replace(/^./, (c) => c.toUpperCase());

function show(v: unknown): string {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    return JSON.stringify(v);
}

const stamp = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? '—'
        : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });
};

export default function DeviceTelemetryV3() {
    const navigate = useNavigate();
    const { orgId, deviceId } = useParams();
    const org = Number(orgId);
    const { getLatestDeviceTelemetry } = useDeviceTelemetry();
    const { getDeviceKeys } = useDeviceKeys(org);

    const [data, setData] = useState<DeviceTelemetry | null>(null);
    /* 'loading' until the first answer; 'none' = nothing (or failed) on the latest poll */
    const [state, setState] = useState<'loading' | 'ok' | 'none'>('loading');
    const [stale, setStale] = useState(false);
    const [deviceName, setDeviceName] = useState<string | null>(null);
    const inFlight = useRef(false);
    const hadData = useRef(false);

    const fetchOnce = useCallback(async () => {
        if (!org || !deviceId || inFlight.current) return;
        inFlight.current = true;
        try {
            const t = await getLatestDeviceTelemetry(org, deviceId);
            if (t) {
                hadData.current = true;
                setData(t);
                setStale(false);
                setState('ok');
            } else if (hadData.current) {
                // keep showing the last good reading
                setStale(true);
            } else {
                setState('none');
            }
        } finally {
            inFlight.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [org, deviceId]);

    useEffect(() => {
        let live = true;
        const tick = () => {
            if (live && document.visibilityState !== 'hidden') fetchOnce();
        };
        fetchOnce();
        const id = window.setInterval(tick, POLL_MS);
        return () => {
            live = false;
            window.clearInterval(id);
        };
    }, [fetchOnce]);

    useEffect(() => {
        if (!org || !deviceId) return;
        let live = true;
        getDeviceKeys()
            .then((keys: DeviceKey[]) => {
                const k = (keys ?? []).find((x) => x.keyId === deviceId);
                if (live && k) setDeviceName(k.name);
            })
            .catch(() => undefined);
        return () => {
            live = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [org, deviceId]);

    const td = (data?.telemetryData ?? {}) as Record<string, any>;
    const battery = td.battery as { level?: number; state?: string } | undefined;
    const network = td.network as { networkSSID?: string; connectivity?: string } | undefined;
    const extra = Object.entries(td).filter(([k]) => k !== 'battery' && k !== 'network');
    const os = [data?.deviceOsName, data?.deviceOsVersion].filter(Boolean).join(' ');

    return (
        <AppShell>
            <div className="dv-page dets">
                <Crumb label="Devices" onClick={() => navigate('/device-management#keys')} />
                <PageHeadActions
                    title={deviceName ?? deviceId ?? 'Device'}
                    sub={
                        state === 'ok'
                            ? `${seen(data?.updatedAt ?? data?.createdAt).replace('Seen', 'Reported')}${stale ? ' · last refresh failed' : ' · refreshes every 10s'}`
                            : 'Device telemetry'
                    }
                />

                {state === 'loading' && (
                    <TCard>
                        <TSkeleton rows={4} />
                    </TCard>
                )}

                {state === 'none' && (
                    <TCard>
                        <TEmpty
                            message="No telemetry from this device yet. It appears once the device reports in."
                            actionLabel="Check again"
                            onAction={fetchOnce}
                        />
                    </TCard>
                )}

                {state === 'ok' && data && (
                    <div className="stack">
                        {(typeof battery?.level === 'number' || battery?.state || network?.connectivity || network?.networkSSID) && (
                            <div className="dv-tiles">
                                {typeof battery?.level === 'number' && (
                                    <div className="tile">
                                        <p className="tl">Battery</p>
                                        <p className="tn" style={{ margin: 0 }}>
                                            {battery.level.toFixed(0)}%
                                        </p>
                                        {battery.state && <p className="tl" style={{ margin: '4px 0 0' }}>{battery.state}</p>}
                                    </div>
                                )}
                                {typeof battery?.level !== 'number' && battery?.state && (
                                    <div className="tile">
                                        <p className="tl">Battery</p>
                                        <p className="tn" style={{ margin: 0 }}>{battery.state}</p>
                                    </div>
                                )}
                                {(network?.connectivity || network?.networkSSID) && (
                                    <div className="tile">
                                        <p className="tl">Network</p>
                                        <p className="tn" style={{ margin: 0 }}>
                                            {network.networkSSID || network.connectivity}
                                        </p>
                                        {network.networkSSID && network.connectivity && (
                                            <p className="tl" style={{ margin: '4px 0 0' }}>{network.connectivity}</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        <SectionCard title="Device">
                            <Frow label="Key ID">
                                <span className="dv-mono">{data.deviceKeyId || deviceId}</span>
                            </Frow>
                            <Frow label="Device ID">
                                <span className="dv-mono">{show(data.deviceId)}</span>
                            </Frow>
                            <Frow label="Type">{show(data.deviceType)}</Frow>
                            <Frow label="Brand">{show(data.deviceBrand)}</Frow>
                            <Frow label="Model">{show(data.deviceModel)}</Frow>
                            <Frow label="OS">{os || '—'}</Frow>
                            <Frow label="OS build">{show(data.deviceOsVersionNumber)}</Frow>
                            <Frow label="Last updated">{stamp(data.updatedAt ?? data.createdAt)}</Frow>
                        </SectionCard>

                        {extra.length > 0 && (
                            <SectionCard title="Telemetry" note="As reported by the device">
                                {extra.map(([k, v]) => (
                                    <Frow key={k} label={humanKey(k)}>
                                        <span className={typeof v === 'object' && v !== null ? 'dv-mono' : undefined}>
                                            {show(v)}
                                        </span>
                                    </Frow>
                                ))}
                            </SectionCard>
                        )}
                    </div>
                )}
            </div>
        </AppShell>
    );
}
